import { useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  CampoDeSelecao,
  CampoDeTexto,
  IconeLixeira,
  Modal,
  Selo,
  useDialogo,
} from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { lerComoBase64 } from '../../servicos/arquivo';
import type { DocumentoDaLinha, LinhaCronograma } from '../../servicos/operacao';
import { ErroDaApi } from '../ErroDaApi';

interface Anexo {
  readonly chave: string;
  readonly localizacao: string;
  readonly nome: string;
  /** O documento do fluxo em português — "Certificado de transferência". */
  readonly rotulo: string;
  /** Link assinado de download (120 dias). */
  readonly url: string;
}

interface EstadoDaAprovacao {
  readonly id: string;
  readonly emailId: string;
  readonly status: string;
  readonly aprovacao: boolean;
  readonly template: string | null;
  readonly aprovadoPor: string | null;
  readonly aprovadoEm: string | null;
  readonly origem: string | null;
  readonly anexos: readonly Anexo[];
  readonly linkAtivo: boolean;
}

/**
 * A aprovação de um item, vista pelo escritório (RN-079).
 *
 * O decisor normalmente responde pelo link do e-mail. Esta tela é para os outros
 * casos: ver o que ele anexou, registrar um "sim" que veio por telefone, e
 * desfazer o anexo errado.
 *
 * ## Por que mostrar a origem
 *
 * `manual:fulano@…` e o e-mail do respondente ocupam o mesmo campo, e a
 * diferença importa numa auditoria: uma é o escritório afirmando que a aprovação
 * existiu, a outra é o próprio decisor tendo clicado. A tela separa as duas em
 * vez de exibir só "aprovado".
 */
export function ModalDeAprovacao(props: {
  readonly clienteId: string;
  readonly item: LinhaCronograma | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const id = props.item?.id ?? null;

  const estado = useQuery({
    queryKey: ['aprovacao', id],
    queryFn: () => requisicaoAutenticada<EstadoDaAprovacao>(`/icms/emails/${id ?? ''}/aprovacao`),
    enabled: id !== null,
  });

  const [template, setTemplate] = useState('CLIENTE_APROVA_CREDITO');
  const [observacao, setObservacao] = useState('');

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['aprovacao', id] });
    void queryClient.invalidateQueries({ queryKey: ['cronograma', props.clienteId] });
  };

  const registrar = useMutation({
    mutationFn: (aprovacao: boolean) =>
      requisicaoAutenticada<EstadoDaAprovacao>(`/icms/emails/${id ?? ''}/aprovacao`, {
        metodo: 'POST',
        corpo: {
          aprovacao,
          ...(aprovacao ? { template } : {}),
          ...(observacao.trim() === '' ? {} : { observacao: observacao.trim() }),
        },
      }),
    onSuccess: invalidar,
  });

  // O tipo de anexo já vem escolhido pelo passo (a API diz qual documento a
  // etapa espera): no pedido ao fornecedor, o certificado de transferência; no
  // certificado ao cliente, o de apropriação; no pagamento, o boleto. Trocar
  // continua possível.
  const [chaveEscolhida, setChave] = useState<string | null>(null);
  const chave = chaveEscolhida ?? anexoPadraoDoPasso(props.item);
  /*
    A mesma entrada de arquivo serve ao seletor de tipo e ao "Anexar este" de
    cada documento: clicar no documento escolhe o tipo **e** abre a janela, em
    vez de só mexer num `select` que o operador nem estava olhando.
  */
  const entradaDeArquivo = useRef<HTMLInputElement>(null);

  const escolherArquivo = (chaveDoAnexo: string): void => {
    setChave(chaveDoAnexo);
    entradaDeArquivo.current?.click();
  };

  const anexar = useMutation({
    mutationFn: async (arquivo: File) =>
      requisicaoAutenticada<unknown>(`/icms/emails/${id ?? ''}/attachments`, {
        metodo: 'POST',
        corpo: {
          chave,
          nome: arquivo.name,
          conteudoBase64: await lerComoBase64(arquivo),
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
        },
      }),
    onSuccess: invalidar,
  });

  // Com localização, sai um anexo só; sem ela, saem todos.
  const removerAnexos = useMutation({
    mutationFn: (localizacao: string | undefined) =>
      requisicaoAutenticada<{ removidos: number }>(
        `/icms/emails/${id ?? ''}/attachments${
          localizacao === undefined ? '' : `?localizacao=${encodeURIComponent(localizacao)}`
        }`,
        { metodo: 'DELETE' },
      ),
    onSuccess: invalidar,
  });

  const fechar = (): void => {
    registrar.reset();
    removerAnexos.reset();
    setObservacao('');
    setChave(null);
    props.aoFechar();
  };

  const dados = estado.data;

  return (
    <Modal
      aberto={props.item !== null}
      aoFechar={fechar}
      titulo="Detalhes do e-mail"
      descricao={props.item === null ? undefined : props.item.titulo}
      largura="larga"
      rodape={<Botao onClick={fechar}>Fechar</Botao>}
    >
      <div className="flex flex-col gap-5">
        {estado.isError && <ErroDaApi titulo="Não foi possível carregar" erro={estado.error} />}
        {estado.isPending && id !== null && (
          <p className="py-6 text-center text-sm text-conteudo-sutil">Carregando…</p>
        )}

        {props.item !== null && (
          <>
            <Aviso tom={TOM_DA_ACAO[props.item.proximaAcao.tom]} titulo="Próxima ação">
              {props.item.proximaAcao.detalhe}
            </Aviso>

            <Aviso tom="informacao" titulo="O que acontece neste passo">
              {props.item.passo?.oQueAcontece ??
                'E-mail enviado fora da fila (transição do contrato ou envio avulso). Fica registrado aqui para controle.'}
            </Aviso>

            {props.item.documentos.length > 0 && (
              <DocumentosDoPasso documentos={props.item.documentos} aoAnexar={escolherArquivo} />
            )}
          </>
        )}

        {dados !== undefined && (
          <>
            <AnexosDoItem
              dados={dados}
              chave={chave}
              aoTrocarChave={setChave}
              entradaDeArquivo={entradaDeArquivo}
              anexar={anexar}
              removerAnexos={removerAnexos}
            />

            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <Linha rotulo="Situação">
                <Selo tom={dados.aprovacao ? 'positivo' : 'neutro'}>
                  {dados.aprovacao ? 'Aprovado' : 'Sem aprovação registrada'}
                </Selo>
              </Linha>
              <Linha rotulo="Link de resposta">
                <Selo tom={dados.linkAtivo ? 'progresso' : 'neutro'}>
                  {dados.linkAtivo ? 'Ativo' : 'Usado ou expirado'}
                </Selo>
              </Linha>
              {dados.aprovadoPor !== null && (
                <Linha rotulo="Registrado por">{dados.aprovadoPor}</Linha>
              )}
              {dados.aprovadoEm !== null && (
                <Linha rotulo="Data">
                  {new Date(dados.aprovadoEm).toLocaleString('pt-BR', { timeZone: 'UTC' })}
                </Linha>
              )}
              {dados.origem !== null && (
                <Linha rotulo="Origem">
                  {dados.origem.startsWith('manual:')
                    ? `Registro manual — ${dados.origem.slice(7)}`
                    : `Resposta pelo link — ${dados.origem}`}
                </Linha>
              )}
              {dados.template !== null && (
                <Linha rotulo="Tipo de aprovação">{humanizar(dados.template)}</Linha>
              )}
            </dl>

            <section className="flex flex-col gap-3 rounded-card border border-borda bg-superficie-elevada p-4">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Registrar decisão manualmente
              </h3>
              <p className="text-xs text-conteudo-secundario">
                Use quando a resposta veio por fora do link — telefone, e-mail respondido, reunião.
                Fica marcada como registro manual, separada de quem clicou.
              </p>

              <CampoDeSelecao
                rotulo="Tipo de aprovação"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                opcoes={[
                  { valor: 'CLIENTE_APROVA_CREDITO', rotulo: 'Cliente aprova o crédito' },
                  {
                    valor: 'FORNECEDOR_CERTIFICADO_TRANSF',
                    rotulo: 'Fornecedor — certificado de transferência',
                  },
                  { valor: 'ASSESSOR_HONORARIOS', rotulo: 'Assessor — honorários' },
                ]}
              />
              <CampoDeTexto
                rotulo="Observação"
                rows={2}
                ajuda="Opcional. Substitui a descrição da linha — registre onde a decisão aconteceu."
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
              />

              <div className="flex flex-wrap gap-2">
                <Botao carregando={registrar.isPending} onClick={() => registrar.mutate(true)}>
                  Registrar aprovação
                </Botao>
                <Botao
                  variante="secundario"
                  carregando={registrar.isPending}
                  onClick={() => registrar.mutate(false)}
                >
                  Registrar reprovação
                </Botao>
              </div>

              {registrar.isSuccess && (
                <Aviso tom="positivo" titulo="Decisão registrada">
                  Gravada como registro manual, com o seu e-mail e a data de agora.
                </Aviso>
              )}
              {registrar.isError && (
                <ErroDaApi titulo="Não foi possível registrar" erro={registrar.error} />
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

function Linha(props: {
  readonly rotulo: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.children}</dd>
    </div>
  );
}

function humanizar(chave: string): string {
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/* ─── Anexos ──────────────────────────────────────────────────────────────── */

function AnexosDoItem(props: {
  readonly dados: EstadoDaAprovacao;
  readonly chave: string;
  readonly aoTrocarChave: (chave: string) => void;
  readonly entradaDeArquivo: React.RefObject<HTMLInputElement>;
  readonly anexar: UseMutationResult<unknown, Error, File>;
  readonly removerAnexos: UseMutationResult<{ removidos: number }, Error, string | undefined>;
}): JSX.Element {
  const { confirmar } = useDialogo();
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-conteudo-primario">
        Anexos ({props.dados.anexos.length})
      </h3>
      {/*
        Anexar pelo lado de dentro: o cliente frequentemente manda o
        certificado por outro canal, e até aqui a única saída era pedir
        que ele reenviasse pelo link do e-mail.
      */}
      <div className="flex flex-wrap items-end gap-3 rounded-card border border-borda p-3">
        <CampoDeSelecao
          rotulo="Tipo do anexo"
          className="w-64"
          value={props.chave}
          onChange={(e) => props.aoTrocarChave(e.target.value)}
          opcoes={[
            {
              valor: 'UPLOAD_CERTIFICADO_TRANSF_MES',
              rotulo: 'Certificado de transferência (fornecedor)',
            },
            {
              valor: 'UPLOAD_CERTIFICADO_APROPRIACAO_MES',
              rotulo: 'Certificado de apropriação (cliente)',
            },
            { valor: 'UPLOAD_BOLETO_PAGAMENTO', rotulo: 'Boleto de pagamento' },
            { valor: 'UPLOAD_NF_MES', rotulo: 'Nota fiscal' },
          ]}
        />
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Arquivo</span>
          <input
            ref={props.entradaDeArquivo}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            disabled={props.anexar.isPending}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo !== undefined) props.anexar.mutate(arquivo);
              e.target.value = '';
            }}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
          />
        </label>
      </div>

      {props.anexar.isError && (
        <ErroDaApi titulo="Não foi possível anexar" erro={props.anexar.error} />
      )}

      {props.dados.anexos.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nenhum documento anexado a este item.</p>
      ) : (
        <>
          <ul className="flex flex-col gap-1">
            {props.dados.anexos.map((a) => (
              <li
                key={a.localizacao}
                className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-borda px-3 py-2 text-sm"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="text-conteudo-primario">{a.rotulo}</span>
                  <span className="truncate text-xs text-conteudo-sutil">{a.nome}</span>
                </span>
                <span className="flex items-center gap-3">
                  {/* O link é assinado e vale 120 dias — o mesmo do e-mail. */}
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-acento-ciano underline-offset-4 hover:underline"
                  >
                    Baixar
                  </a>
                  {/*
                    Uma lixeira por arquivo: quem anexou o certificado do mês
                    errado corrigia derrubando todos os anexos da linha.
                  */}
                  <button
                    type="button"
                    aria-label={`Remover ${a.rotulo} (${a.nome})`}
                    title="Remover este anexo"
                    className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-erro/10 hover:text-erro focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erro"
                    onClick={() => {
                      void (async () => {
                        const ok = await confirmar({
                          titulo: `Remover "${a.nome}" deste e-mail?`,
                          descricao:
                            'O vínculo sai desta linha; o arquivo continua guardado no armazenamento e nos documentos da empresa.',
                          rotuloConfirmar: 'Remover anexo',
                          tom: 'perigo',
                        });
                        if (ok) props.removerAnexos.mutate(a.localizacao);
                      })();
                    }}
                  >
                    <IconeLixeira className="h-4 w-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <Botao
              variante="sutil"
              tamanho="pequeno"
              carregando={props.removerAnexos.isPending}
              onClick={async () => {
                const ok = await confirmar({
                  titulo: `Remover os ${props.dados.anexos.length} anexos deste item?`,
                  descricao:
                    'O vínculo sai da linha e a aprovação fica sem documento até que alguém anexe de novo.\n\nO arquivo em si continua guardado no armazenamento.',
                  rotuloConfirmar: 'Remover anexos',
                  tom: 'perigo',
                });
                if (ok) props.removerAnexos.mutate(undefined);
              }}
            >
              Remover todos
            </Botao>
            <span className="text-xs text-conteudo-sutil">
              Some o vínculo, não o arquivo — o documento fiscal continua guardado.
            </span>
          </div>
        </>
      )}
      {props.removerAnexos.isError && (
        <ErroDaApi titulo="Não foi possível remover" erro={props.removerAnexos.error} />
      )}
    </section>
  );
}

/**
 * Os documentos que a etapa envolve — o que já chegou e o que falta.
 *
 * É a resposta visual para "o que o cliente subiu, o que o fornecedor ainda
 * deve": cada documento diz de quem é e, quando falta, o botão já deixa o
 * seletor de anexo na chave certa.
 */
function DocumentosDoPasso(props: {
  readonly documentos: readonly DocumentoDaLinha[];
  /** Escolhe o tipo **e** abre a janela de arquivo — é o que "Anexar este" faz. */
  readonly aoAnexar: (chave: string) => void;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-conteudo-primario">Documentos desta etapa</h3>
      <ul className="flex flex-col gap-2">
        {props.documentos.map((d) => (
          <li
            key={`${d.chave}-${d.papel}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-3 py-2"
          >
            <span className="flex flex-col">
              <span className="text-sm text-conteudo-primario">{d.rotulo}</span>
              <span className="text-xs text-conteudo-sutil">
                {d.papel === 'RESPOSTA'
                  ? `Resposta esperada — ${d.deRotulo.toLowerCase()} envia pelo link do e-mail`
                  : 'Vai anexado neste e-mail'}
              </span>
            </span>
            <span className="flex items-center gap-2">
              <Selo tom={d.recebido ? 'positivo' : d.papel === 'RESPOSTA' ? 'atencao' : 'neutro'}>
                {d.recebido ? 'Recebido' : 'Falta'}
              </Selo>
              {!d.recebido && (
                <Botao variante="sutil" tamanho="pequeno" onClick={() => props.aoAnexar(d.chave)}>
                  Anexar este
                </Botao>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** O anexo que o passo espera — o seletor já abre nele. */
function anexoPadraoDoPasso(item: LinhaCronograma | null): string {
  const esperado = item?.documentos.find((d) => d.papel === 'RESPOSTA');
  return esperado?.chave ?? item?.documentos[0]?.chave ?? 'UPLOAD_CERTIFICADO_TRANSF_MES';
}

const TOM_DA_ACAO = {
  concluido: 'positivo',
  aguardando: 'atencao',
  atencao: 'erro',
  neutro: 'informacao',
} as const;
